import React, { useState, useEffect, useRef } from 'react';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import {
  Brain, TrendingUp, TrendingDown, Activity, Shield, Zap, Heart,
  AlertTriangle, ChevronRight, Loader2, Eye, CheckCircle,
  RefreshCw, Clock, Minus
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════
// STATE CONFIG
// ═══════════════════════════════════════════════════════════

const STATE_CONFIG = {
  calm_alignment:        { color: '#34d399', glow: '#34d39940', label: 'Calm', icon: CheckCircle, category: 'optimal' },
  performative_flow:     { color: '#a78bfa', glow: '#a78bfa40', label: 'Flow', icon: Zap, category: 'optimal' },
  nervous_system_strain: { color: '#f87171', glow: '#f8717140', label: 'Strain', icon: AlertTriangle, category: 'dysregulation' },
  cognitive_overload:    { color: '#fbbf24', glow: '#fbbf2440', label: 'Overload', icon: Brain, category: 'dysregulation' },
  anxiety_loop:          { color: '#fb7185', glow: '#fb718540', label: 'Anxiety', icon: RefreshCw, category: 'dysregulation' },
  emotional_fatigue:     { color: '#c084fc', glow: '#c084fc40', label: 'Fatigue', icon: Heart, category: 'depletion' },
  recovery_debt:         { color: '#94a3b8', glow: '#94a3b840', label: 'Recovery', icon: Clock, category: 'depletion' },
  fragmented_focus:      { color: '#fb923c', glow: '#fb923c40', label: 'Fragment', icon: Activity, category: 'dysregulation' },
  irritable_reactivity:  { color: '#ef4444', glow: '#ef444440', label: 'Reactive', icon: Zap, category: 'dysregulation' },
};

const MARKER_TYPES = {
  early_warning: { icon: AlertTriangle, color: '#fbbf24', label: 'Early Warning' },
  repeat_trigger: { icon: RefreshCw, color: '#fb7185', label: 'Repeat Trigger' },
  recovery_point: { icon: CheckCircle, color: '#34d399', label: 'Recovery' },
  peak_stress: { icon: Zap, color: '#ef4444', label: 'Peak Stress' },
  flow_state: { icon: Activity, color: '#a78bfa', label: 'Flow' },
};

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════

export default function TwinFloEvolution({ subscriberId, isStaff = true }) {
  const [timeline, setTimeline] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [signature, setSignature] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayRange, setDayRange] = useState(14);
  const [mode, setMode] = useState('therapeutic');
  const svgRef = useRef(null);

  useEffect(() => {
    if (!subscriberId) return;
    setLoading(true);
    const prefix = isStaff ? '' : '';
    Promise.all([
      axios.get(`${API}/twinflo/timeline/${subscriberId}?days=${dayRange}`).catch(() => ({ data: {} })),
      axios.get(`${API}/twinflo/predictions/${subscriberId}`).catch(() => ({ data: { predictions: [] } })),
      axios.get(`${API}/twinflo/signature/${subscriberId}`).catch(() => ({ data: {} })),
    ]).then(([tlRes, predRes, sigRes]) => {
      setTimeline(tlRes.data);
      setPredictions(predRes.data?.predictions || []);
      setSignature(sigRes.data?.signature_profile || null);
    }).finally(() => setLoading(false));
  }, [subscriberId, dayRange, isStaff]);

  if (loading) return (
    <div className="flex justify-center py-16" data-testid="twinflo-evolution-loading">
      <Loader2 className="w-5 h-5 text-adm-accent animate-spin" />
    </div>
  );

  const tl = timeline?.timeline || [];
  const patterns = timeline?.patterns || [];
  const trend = timeline?.trend_summary || {};

  if (!tl.length) return (
    <Card className="bg-[#0d1117] border-white/[0.04]">
      <CardContent className="py-12 text-center">
        <Brain className="w-8 h-8 text-white/10 mx-auto mb-3" />
        <p className="text-xs text-white/30">No evolution data available yet</p>
      </CardContent>
    </Card>
  );

  const latest = tl[tl.length - 1];
  const latestConfig = STATE_CONFIG[latest?.state] || STATE_CONFIG.calm_alignment;

  // Build signature markers
  const markers = {};
  if (signature) {
    tl.forEach((t, i) => {
      const s = t.state;
      if (s === 'performative_flow') markers[i] = 'flow_state';
      else if (s === 'calm_alignment' && i > 0 && tl[i-1].state !== 'calm_alignment') markers[i] = 'recovery_point';
      else if (i > 0 && tl[i-1].state === 'calm_alignment' && s !== 'calm_alignment' && s !== 'performative_flow') markers[i] = 'early_warning';
      else if (t.regulation != null && t.regulation < 20) markers[i] = 'peak_stress';
    });
    // Repeat triggers
    const stateCounts = {};
    tl.forEach((t, i) => {
      const s = t.state;
      stateCounts[s] = (stateCounts[s] || 0) + 1;
      if (stateCounts[s] >= 3 && s !== 'calm_alignment' && !markers[i]) markers[i] = 'repeat_trigger';
    });
  }

  // SVG curve points
  const nodeWidth = Math.max(44, Math.min(64, 800 / tl.length));
  const svgWidth = tl.length * nodeWidth + 120; // extra for prediction zone
  const svgHeight = 140;
  const baseY = 100;
  const curvePoints = tl.map((t, i) => {
    const reg = t.regulation ?? 50;
    const y = baseY - (reg * 0.7);
    const x = 20 + i * nodeWidth + nodeWidth / 2;
    return { x, y, ...t, index: i };
  });

  // Prediction extension points
  const predPoints = predictions.slice(0, 3).map((p, i) => {
    const lastPoint = curvePoints[curvePoints.length - 1];
    const severity = p.severity === 'positive' ? -15 : p.severity === 'high' ? 25 : 15;
    return {
      x: lastPoint.x + (i + 1) * nodeWidth * 0.8,
      y: Math.max(10, Math.min(baseY - 5, lastPoint.y + severity * (i + 1))),
      label: p.label,
      severity: p.severity,
    };
  });

  // Build SVG path (smooth bezier)
  const buildPath = (points) => {
    if (points.length < 2) return '';
    let d = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const cpx = (prev.x + curr.x) / 2;
      d += ` C ${cpx} ${prev.y}, ${cpx} ${curr.y}, ${curr.x} ${curr.y}`;
    }
    return d;
  };

  const mainPath = buildPath(curvePoints);
  const allPredPoints = curvePoints.length > 0 ? [curvePoints[curvePoints.length - 1], ...predPoints] : [];
  const predPath = buildPath(allPredPoints);

  return (
    <div className="space-y-4" data-testid="twinflo-evolution">
      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: latestConfig.glow }}>
            <latestConfig.icon className="w-5 h-5" style={{ color: latestConfig.color }} />
          </div>
          <div>
            <div className="text-[10px] text-white/40 uppercase tracking-widest font-['Work_Sans']">TwinFlo Evolution</div>
            <div className="text-lg font-['Outfit'] font-semibold" style={{ color: latestConfig.color }}>
              {latest.state?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </div>
          </div>
          <Badge className={`text-[9px] border-0 ml-2 ${trend.direction === 'improving' ? 'bg-emerald-500/15 text-emerald-400' : trend.direction === 'worsening' ? 'bg-rose-500/15 text-rose-400' : 'bg-white/5 text-white/40'}`}>
            {trend.direction === 'improving' ? <TrendingUp className="w-3 h-3 mr-1" /> : trend.direction === 'worsening' ? <TrendingDown className="w-3 h-3 mr-1" /> : <Minus className="w-3 h-3 mr-1" />}
            {trend.direction}
          </Badge>
          <Badge className={`text-[9px] border-0 ${trend.stability === 'stable' ? 'bg-emerald-500/10 text-emerald-400' : trend.stability === 'unstable' ? 'bg-rose-500/10 text-rose-400' : 'bg-amber-500/10 text-amber-400'}`}>
            {trend.stability}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {/* Mode Toggle */}
          <div className="flex bg-white/[0.03] rounded-lg p-0.5 border border-white/[0.06]">
            {['therapeutic', 'performative'].map(m => (
              <button key={m} onClick={() => setMode(m)} className={`px-2.5 py-1 rounded-md text-[10px] transition-colors ${mode === m ? 'bg-adm-accent/15 text-adm-accent' : 'text-white/30 hover:text-white/50'}`} data-testid={`mode-${m}`}>
                {m === 'therapeutic' ? 'Therapeutic' : 'Performative'}
              </button>
            ))}
          </div>
          {/* Day Range */}
          <div className="flex bg-white/[0.03] rounded-lg p-0.5 border border-white/[0.06]">
            {[7, 14, 30].map(d => (
              <button key={d} onClick={() => setDayRange(d)} className={`px-2 py-1 rounded-md text-[10px] transition-colors ${dayRange === d ? 'bg-white/10 text-white/80' : 'text-white/30 hover:text-white/50'}`} data-testid={`range-${d}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── State Flow Curve (Core Visual) ── */}
      <Card className="bg-[#0d1117] border-white/[0.04] overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#ffffff10 transparent' }}>
            <svg ref={svgRef} width={svgWidth} height={svgHeight + 30} className="min-w-full" data-testid="evolution-svg">
              <defs>
                <linearGradient id="curveGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  {curvePoints.map((p, i) => {
                    const cfg = STATE_CONFIG[p.state] || STATE_CONFIG.calm_alignment;
                    return <stop key={i} offset={`${(i / Math.max(curvePoints.length - 1, 1)) * 100}%`} stopColor={cfg.color} />;
                  })}
                </linearGradient>
                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="0.06" />
                  <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                </linearGradient>
              </defs>

              {/* Area fill under curve */}
              {curvePoints.length >= 2 && (
                <path d={`${mainPath} L ${curvePoints[curvePoints.length-1].x} ${baseY} L ${curvePoints[0].x} ${baseY} Z`} fill="url(#areaGrad)" />
              )}

              {/* Main curve */}
              <path d={mainPath} fill="none" stroke="url(#curveGrad)" strokeWidth="2.5" strokeLinecap="round" />

              {/* Prediction curve (dotted) */}
              {predPath && (
                <path d={predPath} fill="none" stroke="#ffffff20" strokeWidth="1.5" strokeDasharray="6 4" strokeLinecap="round" />
              )}

              {/* Pattern highlight bands */}
              {patterns.map((pat, pi) => {
                if (pat.pattern_type === 'persistent_state') {
                  const matchIdx = curvePoints.filter(p => p.state === pat.state);
                  if (matchIdx.length >= 2) {
                    const first = matchIdx[0];
                    const last = matchIdx[matchIdx.length - 1];
                    return <rect key={pi} x={first.x - nodeWidth/2} y={2} width={last.x - first.x + nodeWidth} height={svgHeight - 4} rx="4" fill="#ffffff04" stroke="#ffffff08" strokeWidth="0.5" />;
                  }
                }
                return null;
              })}

              {/* Baseline reference line */}
              <line x1="10" y1={baseY - 35} x2={svgWidth - 10} y2={baseY - 35} stroke="#ffffff08" strokeDasharray="2 4" />
              <text x="6" y={baseY - 38} fill="#ffffff20" fontSize="7" fontFamily="Work Sans">50</text>

              {/* Nodes */}
              {curvePoints.map((p, i) => {
                const cfg = STATE_CONFIG[p.state] || STATE_CONFIG.calm_alignment;
                const intensity = p.intensity ?? 0.5;
                const r = 5 + intensity * 6;
                const isSelected = selectedDay === i;
                const marker = markers[i];

                return (
                  <g key={i} onClick={() => setSelectedDay(isSelected ? null : i)} style={{ cursor: 'pointer' }}>
                    {/* Glow */}
                    <circle cx={p.x} cy={p.y} r={r + 4} fill={isSelected ? cfg.glow : 'transparent'} />
                    {/* Node */}
                    <circle cx={p.x} cy={p.y} r={r} fill={cfg.color} opacity={isSelected ? 1 : 0.8} />
                    {/* Inner dot */}
                    <circle cx={p.x} cy={p.y} r={2} fill="#0d1117" />
                    {/* Marker icon */}
                    {marker && (
                      <circle cx={p.x} cy={p.y - r - 6} r="4" fill={MARKER_TYPES[marker]?.color || '#fff'} opacity="0.8" />
                    )}
                    {/* Date label (first, last, and every 3rd) */}
                    {(i === 0 || i === curvePoints.length - 1 || i % 3 === 0) && (
                      <text x={p.x} y={svgHeight + 12} textAnchor="middle" fill="#ffffff30" fontSize="8" fontFamily="Work Sans">
                        {p.date?.slice(5)}
                      </text>
                    )}
                  </g>
                );
              })}

              {/* Prediction nodes (dotted zone) */}
              {predPoints.map((p, i) => {
                const fillColor = p.severity === 'positive' ? '#34d399' : p.severity === 'high' ? '#f87171' : '#fbbf24';
                return (
                  <g key={`pred-${i}`}>
                    <circle cx={p.x} cy={p.y} r="5" fill={fillColor} opacity="0.4" strokeDasharray="2 2" stroke={fillColor} strokeWidth="1" />
                    <text x={p.x} y={p.y - 12} textAnchor="middle" fill={fillColor} fontSize="7" fontFamily="Work Sans" opacity="0.6">{p.label?.split(' ')[0]}</text>
                  </g>
                );
              })}

              {/* "Predicted" label */}
              {predPoints.length > 0 && (
                <text x={predPoints[0].x - 10} y={svgHeight + 12} fill="#ffffff15" fontSize="7" fontFamily="Work Sans">predicted</text>
              )}
            </svg>
          </div>
        </CardContent>
      </Card>

      {/* ── Day Detail Panel (on click) ── */}
      {selectedDay !== null && tl[selectedDay] && (
        <Card className="bg-[#0d1117] border-white/[0.06]" data-testid="day-detail-panel">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: (STATE_CONFIG[tl[selectedDay].state] || STATE_CONFIG.calm_alignment).color }} />
                <span className="text-sm font-['Outfit'] font-medium text-white">{tl[selectedDay].date}</span>
                <span className="text-xs text-white/40">{tl[selectedDay].state?.replace(/_/g, ' ')}</span>
              </div>
              <button onClick={() => setSelectedDay(null)} className="text-white/20 hover:text-white/50 text-xs">close</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              {[
                { label: 'Regulation', value: tl[selectedDay].regulation, good: 'high' },
                { label: 'Emotional Load', value: tl[selectedDay].emotional_load, good: 'low' },
                { label: 'Resilience', value: tl[selectedDay].resilience, good: 'high' },
                { label: 'Intensity', value: Math.round((tl[selectedDay].intensity || 0) * 100), good: 'low' },
              ].map(s => {
                const val = s.value ?? 0;
                const isGood = s.good === 'high' ? val >= 50 : val <= 40;
                return (
                  <div key={s.label} className="bg-white/[0.03] rounded-lg p-2">
                    <div className="text-[9px] text-white/30 mb-0.5">{s.label}</div>
                    <div className={`text-sm font-semibold ${isGood ? 'text-emerald-400' : 'text-amber-400'}`}>{val}</div>
                  </div>
                );
              })}
            </div>
            {tl[selectedDay].drivers?.length > 0 && (
              <div>
                <div className="text-[9px] text-white/30 uppercase tracking-wider mb-1">Drivers</div>
                <div className="flex flex-wrap gap-1">
                  {tl[selectedDay].drivers.map((d, i) => (
                    <Badge key={i} className="text-[8px] bg-white/[0.04] text-white/50 border-0">{d.replace(':', ': ')}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Patterns + Markers Legend ── */}
      {(patterns.length > 0 || Object.keys(markers).length > 0) && (
        <div className="flex flex-wrap gap-2">
          {patterns.map((p, i) => (
            <div key={i} className="flex items-center gap-1.5 bg-white/[0.02] rounded-lg px-2.5 py-1.5 border border-white/[0.04]">
              <Eye className="w-3 h-3 text-adm-accent" />
              <span className="text-[10px] text-white/50">{p.description}</span>
            </div>
          ))}
          {Object.values(markers).length > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              {Object.entries(MARKER_TYPES).filter(([k]) => Object.values(markers).includes(k)).map(([k, v]) => (
                <div key={k} className="flex items-center gap-1 text-[8px]" style={{ color: v.color }}>
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: v.color }} />
                  {v.label}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
